import React from "react";
import classNames from "classnames";

export const DEFAULT_STEPS = [
  { title: "Account", subtitle: "Log In or Create Your Account" },
  { title: "Select Your Service", subtitle: "Choose Your Specialty" },
  {
    title: "Visit Details",
    subtitle: "Provide Your Medical Information",
  },
  { title: "Review Visit Details", subtitle: "Submit Your Visit" },
];

function StepCircle({ number, isActive }) {
  return (
    <div
      className={classNames(
        "flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold",
        isActive
          ? "bg-gradient-to-r from-blue-500 to-blue-300 border-blue text-white"
          : "bg-white border-grey-600 text-themeBlack  border-2"
      )}
    >
      {number}
    </div>
  );
}

function AuthStepper({ steps = DEFAULT_STEPS, activeStepIndex = 0 }) {
  return (
    <div className="w-full">
      {/* Desktop: horizontal — circles + connectors in one row, labels centered below */}
      <div className="hidden md:block md:w-full">
        {/* Row 1: circles centered in equal columns; connector halves meet at circle edges */}
        <div className="flex w-full h-10">
          {steps.map((step, index) => (
            <div
              key={index}
              className="flex-1 relative flex justify-center items-center min-w-0 h-10"
            >
              {/* Left half of connector (from previous circle to this one) */}
              {index > 0 && (
                <div
                  className={classNames(
                    "absolute top-1/2 left-0 w-1/2 h-1 -translate-y-1/2 z-0",
                    index < activeStepIndex + 1 ? "bg-[#52BCD7]" : "bg-grey-600"
                  )}
                  aria-hidden
                />
              )}
              {/* Right half of connector (from this circle to next) */}
              {index < steps.length - 1 && (
                <div
                  className={classNames(
                    "absolute top-1/2 left-1/2 w-1/2 h-1 -translate-y-1/2 z-0",
                    index < activeStepIndex ? "bg-[#52BCD7]" : "bg-grey-600"
                  )}
                  aria-hidden
                />
              )}
              <div className="relative z-10">
                <StepCircle number={index + 1} isActive={activeStepIndex >= index} />
              </div>
            </div>
          ))}
        </div>
        {/* Row 2: title + subtitle centered under each circle */}
        <div className="flex w-full mt-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className="flex-1 flex flex-col items-center justify-start min-w-0 px-0.5 text-center"
            >
              <p className="text-2xl font-bold text-black truncate w-full">
                {step.title}
              </p>
              <p className="text-lg font-medium text-themeBlack truncate w-full" title={step.subtitle}>
                {step.subtitle}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: vertical — one line through circle centers, circles on top (Figma ref) */}
      <div className="md:hidden flex w-full">
        {/* Left: line through full height, then circles + spacers so line runs through centers */}
        <div className="relative w-10 flex-shrink-0 flex flex-col">
          {/* Full-height vertical line behind circles */}
          <div
            className="absolute top-0 bottom-0 left-1/2 w-1 -translate-x-1/2 z-0 bg-grey-600"
            aria-hidden
          />
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div className="relative z-10 flex justify-center">
                <StepCircle number={index + 1} isActive={activeStepIndex >= index} />
              </div>
              {index < steps.length - 1 && <div className="flex-1 min-h-[27px]" />}
            </React.Fragment>
          ))}
        </div>
        {/* Right: step labels aligned with circles */}
        <div className="flex flex-col flex-1 min-w-0 ml-8">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-col justify-center py-1">
                <p className="text-xl font-bold text-black">{step.title}</p>
                <p className="text-base font-medium text-themeBlack">
                  {step.subtitle}
                </p>
              </div>
              {index < steps.length - 1 && <div className="flex-1 min-h-[8px]" />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AuthStepper;
